/**
 * Resolve check-in/out for an employee on a calendar day (API + local worklog).
 */
import { attendanceService } from "../services/api";
import { readEmployeeWorklog, formatDurationHms } from "./attendancePunchStorage";
import { getAuthJson, getAuthItem } from "../utils/authStorage";

export function getMaxDateInputValue(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: IST_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(date);

  const values = {};

  for (const part of parts) {
    if (part.type !== "literal") {
      values[part.type] = part.value;
    }
  }

  return `${values.year}-${values.month}-${values.day}`;
}

export function isFutureDateIso(dateIso, now = new Date()) {
  if (!dateIso || !/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) return false;
  return dateIso > getMaxDateInputValue(now);
}

export function clampDateIsoToToday(dateIso, now = new Date()) {
  const max = getMaxDateInputValue(now);
  if (!dateIso) return max;
  return dateIso > max ? max : dateIso;
}

/** Max `HH:mm` for &lt;input type="time" max="..." /&gt; on a given date (today → now, past → end of day). */
export function getMaxTimeInputValueForDate(dateIso, now = new Date()) {
  const effectiveDate = dateIso || getMaxDateInputValue(now);
  if (isFutureDateIso(effectiveDate, now)) return "00:00";
  if (effectiveDate < getMaxDateInputValue(now)) return "23:59";
  const parts = new Intl.DateTimeFormat("en-GB", {
      timeZone: IST_TZ,
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now);

    const values = {};

    for (const part of parts) {
      if (part.type !== "literal") {
        values[part.type] = part.value;
      }
    }

    return `${values.hour}:${values.minute}`;
}

export function dateTimeFromIsoAndTime(
  dateIso,
  timeHHmm
) {
  if (
    !dateIso ||
    !timeHHmm ||
    !/^\d{4}-\d{2}-\d{2}$/.test(dateIso)
  ) {
    return null;
  }

  const m = String(timeHHmm)
    .trim()
    .match(/^(\d{1,2}):(\d{2})$/);

  if (!m) {
    return null;
  }

  const hour = Number(m[1]);
  const minute = Number(m[2]);

  if (
    hour < 0 ||
    hour > 23 ||
    minute < 0 ||
    minute > 59
  ) {
    return null;
  }

  /*
   * datetime-local has no timezone.
   *
   * Convert the entered attendance time explicitly
   * from IST into an actual epoch timestamp.
   */
  const isoIst =
    `${dateIso}T` +
    `${String(hour).padStart(2, "0")}:` +
    `${String(minute).padStart(2, "0")}:00+05:30`;

  const result = new Date(isoIst);

  return Number.isNaN(result.getTime())
    ? null
    : result;
}

export function isFutureDateTime(dateIso, timeHHmm, now = new Date()) {
  if (!dateIso || !timeHHmm) return false;
  if (isFutureDateIso(dateIso, now)) return true;
  const dt = dateTimeFromIsoAndTime(dateIso, timeHHmm);
  if (!dt || Number.isNaN(dt.getTime())) return false;
  return dt.getTime() > now.getTime();
}

/** Clamp a time value so it is not after "now" on the selected date. */
export function clampTimeInputToNow(dateIso, timeHHmm, now = new Date()) {
  if (!timeHHmm) return "";
  if (!isFutureDateTime(dateIso, timeHHmm, now)) return timeHHmm;
  return getMaxTimeInputValueForDate(dateIso, now);
}

/**
 * Validate regularise check-in/out (no future date or times).
 * @returns {{ valid: boolean, errors: string[] }}
 */
export function validateRegulariseCheckTimes({ dateIso, inTime, outTime, now = new Date() }) {
  const errors = [];
  if (!dateIso) {
    errors.push("Select a date.");
  } else if (isFutureDateIso(dateIso, now)) {
    errors.push("Cannot regularise attendance for a future date.");
  }

  const inTrim = String(inTime || "").trim();
  const outTrim = String(outTime || "").trim();

  if (inTrim && isFutureDateTime(dateIso, inTrim, now)) {
    errors.push("Check-in time cannot be in the future.");
  }
  if (outTrim && isFutureDateTime(dateIso, outTrim, now)) {
    errors.push("Check-out time cannot be in the future.");
  }

  if (inTrim && outTrim && dateIso && !isFutureDateIso(dateIso, now)) {
    const inDt = dateTimeFromIsoAndTime(dateIso, inTrim);
    const outDt = dateTimeFromIsoAndTime(dateIso, outTrim);
    if (inDt && outDt && outDt.getTime() <= inDt.getTime()) {
      errors.push("Check-out must be after check-in on the same day.");
    }
  }

  return { valid: errors.length === 0, errors };
}

/** Logged-in user's linked employee id (navbar punch / ESS / regularise). */
export function getLoggedInEmployeeId() {
  try {
    const currentUser = getAuthJson("currentUser", null);
    const id =
      currentUser?.employeeId ??
      currentUser?.employee?.id ??
      currentUser?.employee_id ??
      getAuthItem("employeeId") ??
      localStorage.getItem("hrms_current_employee_id");
    return id != null && id !== "" ? String(id) : null;
  } catch {
    return null;
  }
}

/**
 * Date picker change: block future dates and return a user-visible warning.
 */
export function applyRegulariseDateChange(rawDateIso, now = new Date()) {
  if (!rawDateIso) {
    return { dateIso: getMaxDateInputValue(now), error: null };
  }
  if (isFutureDateIso(rawDateIso, now)) {
    return {
      dateIso: clampDateIsoToToday(rawDateIso, now),
      error: "Cannot regularise attendance for a future date.",
    };
  }
  return { dateIso: rawDateIso, error: null };
}

/**
 * Time picker change: clamp to now on today and warn if user entered a future time.
 */
export function applyRegulariseTimeChange(dateIso, rawTime, fieldLabel = "Time", now = new Date()) {
  const trimmed = String(rawTime || "").trim();
  if (!trimmed) {
    return { time: "", error: null };
  }
  if (isFutureDateIso(dateIso, now)) {
    return { time: "", error: "Select a valid date (not in the future) first." };
  }
  const clamped = clampTimeInputToNow(dateIso, trimmed, now);
  if (isFutureDateTime(dateIso, trimmed, now) || clamped !== trimmed) {
    return {
      time: clamped,
      error: `${fieldLabel} cannot be in the future.`,
    };
  }
  return { time: clamped, error: null };
}

const IST_TZ = "Asia/Kolkata";

export function resolveAttendanceDisplayTimeZone() {
  return IST_TZ;
}

function formatMsAsDisplayDateTime(ms) {
  if (ms == null || !Number.isFinite(ms)) return null;
  const d = new Date(ms);
  const timeZone = resolveAttendanceDisplayTimeZone();
  const datePart = d.toLocaleDateString("en-IN", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
  const timePart = d.toLocaleTimeString("en-IN", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
  return `${datePart} ${timePart}`;
}

/** Parse punch value to epoch ms (ISO datetime or ms number). */
export function parsePunchInstantMs(raw) {
  if (raw == null || raw === "" || raw === "--") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const s = String(raw).trim();
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) return null;
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? null : d.getTime();
}

/**
 * Human-readable date + time in the viewer’s timezone (IST fallback).
 * Plain HH:mm wall-clock values (Regularise form) are shown as entered — no TZ shift.
 */
export function formatPunchDateTimeDisplay(raw, dateIsoFallback = null) {
  if (raw == null || raw === "" || raw === "--") return null;
  if (typeof raw === "number" && Number.isFinite(raw)) {
    return formatMsAsDisplayDateTime(raw);
  }
  const s = String(raw).trim();
  // Wall-clock from Regularise / local maps — keep digits as typed.
  if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(s)) {
    if (dateIsoFallback && /^\d{4}-\d{2}-\d{2}$/.test(dateIsoFallback)) {
      const [y, m, d] = dateIsoFallback.split("-");
      return `${d}/${m}/${y} ${s.length === 5 ? s : s.slice(0, 5)}`;
    }
    return s.length === 5 ? s : s.slice(0, 5);
  }
  if (/^\d{4}-\d{2}-\d{2}T/.test(s) || /^\d{4}-\d{2}-\d{2}/.test(s)) {
    // Naive DATETIME was JSON-stamped with Z — paint UTC digits as the wall clock
    // (09:05Z means 09:05 IST, not 14:35). Offset ISO (+05:30) is a real instant.
    if (/Z$/i.test(s) && !/[+-]\d{2}:?\d{2}/.test(s.replace(/Z$/i, ""))) {
      const d = new Date(s);
      if (!Number.isNaN(d.getTime())) {
        const dd = String(d.getUTCDate()).padStart(2, "0");
        const mm = String(d.getUTCMonth() + 1).padStart(2, "0");
        const yyyy = d.getUTCFullYear();
        const hh = String(d.getUTCHours()).padStart(2, "0");
        const mi = String(d.getUTCMinutes()).padStart(2, "0");
        return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
      }
    }
    const ms = parsePunchInstantMs(s);
    if (ms != null) return formatMsAsDisplayDateTime(ms);
  }
  return s;
}

/** Total hours for day details: API value, else duration from in/out punches. */
export function resolveDayDetailHours(checkInRaw, checkOutRaw, apiHours) {
  const existing =
    apiHours != null &&
    String(apiHours).trim() !== "" &&
    String(apiHours).trim() !== "--"
      ? String(apiHours).trim()
      : null;
  const inMs = parsePunchInstantMs(checkInRaw);
  const outMs = parsePunchInstantMs(checkOutRaw);
  if (inMs != null && outMs != null && outMs > inMs) {
    return formatDurationHms(outMs - inMs);
  }
  return existing || "--";
}

function formatClockFromMs(ms) {
  if (ms == null || !Number.isFinite(ms)) return null;
  return new Date(ms).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

/** Display clock from API/local values (HH:mm, locale clock, or ISO datetime). */
function formatCheckDisplay(raw, dateIsoFallback = null) {
  if (raw == null || raw === "" || raw === "--") return null;
  const formatted = formatPunchDateTimeDisplay(raw, dateIsoFallback);
  if (formatted) return formatted;
  if (typeof raw === "number" && Number.isFinite(raw)) return formatClockFromMs(raw);
  return String(raw).trim();
}

function formatElapsedFromMs(inMs, outMs) {
  if (inMs == null || outMs == null || !Number.isFinite(inMs) || !Number.isFinite(outMs)) {
    return null;
  }
  const diff = Math.max(0, outMs - inMs);
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  return `${h}h ${String(m).padStart(2, "0")}m`;
}

function pickTimeField(record, keys) {
  if (!record) return null;
  for (const k of keys) {
    const v = record[k];
    if (v != null && v !== "" && v !== "--") return String(v).trim();
  }
  return null;
}

function recordMatchesDateIso(record, dateIso) {
  const raw = record?.date;
  if (!raw) return false;
  const s = String(raw).trim().slice(0, 10);
  return s === dateIso;
}

function applyApiRecordToResult(record, result) {
  const apiIn = formatCheckDisplay(
    pickTimeField(record, [
      "checkIn",
      "checkInTime",
      "inTime",
      "firstIn",
      "firstPunchIn",
    ])
  );
  const apiOut = formatCheckDisplay(
    pickTimeField(record, [
      "checkOut",
      "checkOutTime",
      "outTime",
      "lastOut",
      "lastPunchOut",
    ])
  );
  const apiHours = pickTimeField(record, [
    "totalHours",
    "workedHours",
    "workingHours",
    "totalWorkedHours",
  ]);
  if (!apiIn && !apiOut) return result;
  if (!result.sources.includes("system")) result.sources.push("system");
  // Keep local punch clocks when present; API often returns raw ISO datetimes.
  const inMs = parsePunchInstantMs(
    pickTimeField(record, ["checkIn", "checkInTime", "inTime", "firstIn", "firstPunchIn"])
  );
  const outMs = parsePunchInstantMs(
    pickTimeField(record, ["checkOut", "checkOutTime", "outTime", "lastOut", "lastPunchOut"])
  );
  const ghostSameSecond = inMs != null && outMs != null && outMs <= inMs;
  if (apiIn && !result.checkIn) result.checkIn = apiIn;
  if (apiOut && !result.checkOut && !ghostSameSecond) result.checkOut = apiOut;
  // Only use API hours when the day is closed (open sessions tick forever).
  if (apiHours && !ghostSameSecond && (apiOut || result.checkOut)) result.totalHours = apiHours;
  return result;
}

async function fetchHistoryRecord(employeeId, dateIso, preferMyHistory) {
  const [y, m] = dateIso.split("-").map(Number);
  const loggedInId = getLoggedInEmployeeId();
  const isSelf = loggedInId && String(loggedInId) === String(employeeId);

  const loaders = [];
  if (preferMyHistory || isSelf) {
    loaders.push(async () => {
      const response = await attendanceService.getMyAttendanceHistory(m, y);
      const records = Array.isArray(response?.data) ? response.data : [];
      return records.find((r) => recordMatchesDateIso(r, dateIso)) || null;
    });
  }
  loaders.push(async () => {
    const response = await attendanceService.getAttendanceHistory(m, y, 1, 500, "", [
      employeeId,
    ]);
    const records = Array.isArray(response?.data) ? response.data : [];
    return records.find((r) => recordMatchesDateIso(r, dateIso)) || null;
  });

  for (const load of loaders) {
    try {
      const record = await load();
      if (record) return record;
    } catch {
      /* try next source */
    }
  }
  return null;
}

/**
 * @param {string|number} employeeId
 * @param {string} dateIso - YYYY-MM-DD
 * @param {{ preferMyHistory?: boolean }} [options]
 * @returns {Promise<{ hasRecords: boolean, checkIn: string|null, checkOut: string|null, totalHours: string|null, sources: string[] }>}
 */
export async function resolveEmployeeDayChecks(employeeId, dateIso, options = {}) {
  if (!employeeId || !dateIso || !/^\d{4}-\d{2}-\d{2}$/.test(dateIso)) {
    return { hasRecords: false, checkIn: null, checkOut: null, totalHours: null, sources: [] };
  }
  if (isFutureDateIso(dateIso)) {
    return { hasRecords: false, checkIn: null, checkOut: null, totalHours: null, sources: [] };
  }

  const sources = [];
  let checkIn = null;
  let checkOut = null;
  let totalHours = null;

  const worklog = readEmployeeWorklog(employeeId, dateIso);
  if (worklog?.inMs) {
    sources.push("punch");
    checkIn = formatClockFromMs(worklog.inMs);
    if (worklog.outMs != null) {
      checkOut = formatClockFromMs(worklog.outMs);
      totalHours = formatElapsedFromMs(worklog.inMs, worklog.outMs);
    }
  }

  let result = { checkIn, checkOut, totalHours, sources };
  try {
    const record = await fetchHistoryRecord(
      employeeId,
      dateIso,
      options.preferMyHistory === true
    );
    if (record) result = applyApiRecordToResult(record, result);
  } catch {
    /* API optional — local punch still shown */
  }

  checkIn = result.checkIn;
  checkOut = result.checkOut;
  totalHours = result.totalHours;

  const hasRecords =
    (checkIn != null && checkIn !== "") || (checkOut != null && checkOut !== "");

  return { hasRecords, checkIn, checkOut, totalHours, sources: result.sources };
}

export const regularisedChecksSessionKey = (employeeId) =>
  employeeId != null ? `hrms-calendar-regularised-checks-${String(employeeId)}` : null;

/** Duration label from two `HH:mm` values (same day). */
export function computeDurationBetweenTimeInputs(inVal, outVal) {
  const a = String(inVal || "").trim();
  const b = String(outVal || "").trim();
  if (!a || !b) return "";
  const parse = (t) => {
    const m = t.match(/^(\d{1,2}):(\d{2})$/);
    if (!m) return NaN;
    return parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  };
  let start = parse(a);
  let end = parse(b);
  if (Number.isNaN(start) || Number.isNaN(end)) return "";
  if (end < start) end += 24 * 60;
  const diff = end - start;
  if (diff <= 0) return "";
  const hh = Math.floor(diff / 60);
  const mm = diff % 60;
  return `${hh}h ${String(mm).padStart(2, "0")}m`;
}

/**
 * Append or replace a regularised day entry in session storage (for View history).
 */
export function saveRegularisationSessionEntry(employeeId, entry) {
  const key = regularisedChecksSessionKey(employeeId);
  if (!key || !entry) return;
  const day = Number(entry.day);
  const month = Number(entry.month);
  const year = Number(entry.year);
  if (!Number.isFinite(day) || !Number.isFinite(month) || !Number.isFinite(year)) {
    const parts = String(entry.dateIso || entry.date || "")
      .trim()
      .split("-");
    if (parts.length < 3) return;
    const y = parseInt(parts[0], 10);
    const mo = parseInt(parts[1], 10) - 1;
    const d = parseInt(parts[2], 10);
    if (Number.isNaN(y) || Number.isNaN(mo) || Number.isNaN(d)) return;
    entry = { ...entry, year: y, month: mo, day: d };
  }
  const empId = String(employeeId);
  const label =
    entry.label ||
    `${String(entry.day).padStart(2, "0")}-${String(entry.month + 1).padStart(2, "0")}-${entry.year}`;
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    const list = Array.isArray(parsed) ? parsed : [];
    const filtered = list.filter(
      (e) =>
        !(
          String(e.employeeId) === empId &&
          e.year === entry.year &&
          e.month === entry.month &&
          e.day === entry.day
        )
    );
    const next = [
      {
        id: entry.id || `sess-${Date.now()}`,
        employeeId: empId,
        label,
        day: entry.day,
        year: entry.year,
        month: entry.month,
        checkIn: entry.checkIn ?? "--",
        checkOut: entry.checkOut ?? "--",
        totalHours: entry.totalHours ?? "--",
        status: entry.status || "Pending",
        reason: entry.reason || "",
        notes: entry.notes || "",
        savedAt: entry.savedAt || new Date().toISOString(),
      },
      ...filtered,
    ];
    sessionStorage.setItem(key, JSON.stringify(next));
    try {
      window.dispatchEvent(new CustomEvent("hrms-regularisation-history-updated", { detail: { employeeId: empId } }));
    } catch {
      /* ignore */
    }
  } catch {
    /* ignore */
  }
}

/** One entry per calendar day (latest wins), newest first. */
export function loadRegularisationSessionHistory(employeeId) {
  const key = regularisedChecksSessionKey(employeeId);
  if (!key) return [];
  try {
    const raw = sessionStorage.getItem(key);
    const parsed = raw ? JSON.parse(raw) : [];
    if (!Array.isArray(parsed)) return [];
    const empId = String(employeeId);
    const items = parsed.filter((e) => String(e.employeeId) === empId);
    const seen = new Map();
    for (const e of items) {
      const k = `${e.year}-${e.month}-${e.day}`;
      if (!seen.has(k)) seen.set(k, e);
    }
    return Array.from(seen.values()).sort((a, b) => {
      const ta = a.year * 400 + a.month * 32 + a.day;
      const tb = b.year * 400 + b.month * 32 + b.day;
      return tb - ta;
    });
  } catch {
    return [];
  }
}

/** Normalize display/API time to `HH:mm` for &lt;input type="time" /&gt;. */
export function toTimeInputValue(raw) {
  if (raw == null || raw === "" || raw === "--") return "";
  if (typeof raw === "number" && Number.isFinite(raw)) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) {
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
  }
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}T/.test(s)) {
    const d = new Date(s);
    if (!Number.isNaN(d.getTime())) {
      return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    }
  }
  const ampm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*([AaPp][Mm])$/);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    const m = parseInt(ampm[2], 10);
    const ap = ampm[3].toUpperCase();
    if (ap === "PM" && h < 12) h += 12;
    if (ap === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  const hm = s.match(/^(\d{1,2}):(\d{2})(?::\d{2})?$/);
  if (hm) {
    const h = Math.min(23, parseInt(hm[1], 10));
    const m = Math.min(59, parseInt(hm[2], 10));
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  }
  return "";
}

/** Split stored `HH:mm` into 12-hour hour / minute / AM|PM (empty if no time). */
export function hhmmToTwelveHourParts(hhmm) {
  const s = toTimeInputValue(hhmm);
  if (!s) return { hour: "", minute: "", period: "AM" };
  let h = parseInt(s.slice(0, 2), 10);
  const minute = s.slice(3, 5);
  if (!Number.isFinite(h)) return { hour: "", minute: "", period: "AM" };
  const period = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return { hour: String(h), minute, period };
}

/** Build `HH:mm` from 12-hour parts. Empty until hour and minute are both set. */
export function twelveHourPartsToHhmm(hour, minute, period) {
  if (hour === "" || hour == null || minute === "" || minute == null) return "";
  let h = parseInt(hour, 10);
  const m = parseInt(minute, 10);
  if (!Number.isFinite(h) || !Number.isFinite(m) || m < 0 || m > 59) return "";
  const p = String(period || "AM").toUpperCase();
  if (p === "AM") {
    if (h === 12) h = 0;
  } else if (p === "PM") {
    if (h !== 12) h += 12;
  } else {
    return "";
  }
  if (h < 0 || h > 23) return "";
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

/** Display `HH:mm` as `h:mm AM/PM` for typed 12-hour fields. */
export function formatTwelveHourDisplay(hhmm) {
  const p = hhmmToTwelveHourParts(hhmm);
  if (!p.hour || p.minute === "") return "";
  return `${p.hour}:${p.minute} ${p.period}`;
}
